import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { requestConversation } from "@/lib/conversation";

// Selected Work. Editorial trust page.
// Architecture note: built to grow. Future blocks slot in below the logo wall
// without redesigning the page: featured case study (Bluestone first), then
// Case Studies / Campaigns / Behind The Scenes / Awards / Testimonials.
// Until case studies are ready, every logo opens the "being curated" notice.
const BRANDS = [
  { name: "Bluestone", src: "/logos/bluestone.png", h: "h-8 md:h-9" },
  { name: "Titan", src: "/logos/titan.png", h: "h-14 md:h-16" },
  { name: "Fastrack", src: "/logos/fastrack.png", h: "h-12 md:h-14" },
  { name: "Oreo", src: "/logos/oreo.svg", h: "h-8 md:h-9", self: true },
  { name: "Paytm", src: "/logos/paytm.svg", h: "h-8 md:h-9" },
  { name: "TVS", src: "/logos/tvs.svg", h: "h-9 md:h-10" },
  { name: "Yamaha", src: "/logos/yamaha.svg", h: "h-9 md:h-10" },
  { name: "Himalaya", src: "/logos/himalaya.svg", h: "h-10 md:h-12" },
];

function CuratedModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[70] bg-white/70 backdrop-blur-sm flex items-center justify-center px-6 animate-[fadeIn_250ms_ease-out_both]"
      onClick={onClose}
      data-testid="curated-modal"
    >
      <div
        className="w-full max-w-[480px] bg-white border border-bnm rounded-2xl p-8 md:p-12 text-center shadow-[0_24px_80px_rgba(10,10,10,0.10)] animate-[fadeUp_300ms_ease-out_both]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eyebrow eyebrow-denim mb-6">Selected Work</div>
        <h3 className="font-display font-extrabold text-ink tracking-[-0.03em] leading-[1.1] text-[24px] md:text-[28px]">
          Selected work is currently being curated.
        </h3>
        <p className="text-muted-ink text-[15px] md:text-[15.5px] leading-[1.7] mt-5">
          We{"\u2019"}re preparing detailed case studies showcasing our process, production and final work.
        </p>
        <p className="text-ink text-[15px] font-medium mt-4">Coming soon.</p>
        <button
          type="button"
          onClick={onClose}
          className="btn-ghost mt-8 w-full sm:w-auto"
          data-testid="curated-modal-close"
        >
          <span>Close</span>
        </button>
      </div>
    </div>
  );
}

export default function SelectedWork() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white text-ink flex flex-col">
      <Nav />

      <main className="flex-1">
        {/* HERO */}
        <section className="max-w-[1280px] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-14 md:pb-20">
          <div className="max-w-[980px]">
            <Reveal>
              <div className="eyebrow eyebrow-denim mb-8">Selected Work</div>
            </Reveal>
            <Reveal
              as="h1"
              delay={80}
              className="font-display font-extrabold text-ink leading-[0.98] tracking-[-0.045em] text-[40px] sm:text-[60px] md:text-[80px] lg:text-[92px]"
              data-testid="selected-work-headline"
            >
              Trusted by India{"\u2019"}s most
              <br />
              <span className="font-serif-italic bnm-denim-shine font-light">ambitious</span> brands.
            </Reveal>
            <Reveal delay={180}>
              <p className="text-muted-ink text-[16px] md:text-[18px] leading-[1.7] mt-10 md:mt-12 max-w-[640px]">
                Over the years, we{"\u2019"}ve partnered with brands across jewellery, fashion, automotive, retail and FMCG, bringing ideas to life through thoughtful production.
              </p>
            </Reveal>
          </div>
        </section>

        {/* LOGO WALL */}
        <section className="border-t border-bnm">
          <div className="max-w-[1180px] mx-auto px-6 md:px-10 py-20 md:py-32">
            <Reveal>
              <div
                className="grid grid-cols-2 md:grid-cols-4 gap-y-16 md:gap-y-24 gap-x-8 items-center"
                data-testid="selected-work-logo-grid"
              >
                {BRANDS.map((b) => (
                  <button
                    key={b.name}
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className="group flex items-center justify-center py-2 cursor-pointer"
                    data-testid={`work-brand-${b.name.toLowerCase()}`}
                    aria-label={`${b.name}, case study coming soon`}
                  >
                    <img
                      src={b.src}
                      alt={b.name}
                      loading="lazy"
                      className={`${b.self ? "brand-logo-self" : "brand-logo"} w-auto max-w-[150px] md:max-w-[170px] object-contain ${b.h}`}
                    />
                  </button>
                ))}
              </div>
            </Reveal>

            {/* CLOSING STATEMENT */}
            <Reveal delay={140}>
              <p
                className="text-center text-muted-ink text-[14.5px] md:text-[15.5px] leading-[1.7] mt-20 md:mt-28 max-w-[560px] mx-auto"
                data-testid="selected-work-closing"
              >
                From jewellery to automotive, fashion to FMCG, we produce stories across industries.
              </p>
            </Reveal>

            <Reveal delay={200}>
              <div className="mt-10 md:mt-12 flex justify-center">
                <button
                  type="button"
                  onClick={() => {
                    requestConversation();
                    navigate("/");
                  }}
                  className="btn-denim"
                  data-testid="selected-work-cta"
                >
                  <span>Let{"\u2019"}s Begin</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <Footer />

      <CuratedModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
